import { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { Disclosure } from "@headlessui/react";
import classNames from "classnames";
import { NavLink, useParams } from "react-router-dom";
import { useCourseCustomLabels } from "@/react/components/Spaces/CourseSpace/Shared/useCourseCustomLabels";
import { drippedAtTippyContent } from "@/react/helpers/courseHelpers";
import { Icon } from "@circle-react/components/shared/Icon";
import { LoaderRow } from "@circle-react/components/shared/LoaderRow";
import {
  useCurrentSpaceContext,
  useSpaceResource,
} from "@circle-react/contexts";
import { coursePaths } from "@circle-react/helpers/urlHelpers";
import { useSmScreenMediaQuery } from "@circle-react/hooks/useMediaQuery";
import { TippyV2 } from "@circle-react-shared/TippyV2";
import { Typography } from "@circle-react-uikit/Typography";

export const TOCContent = ({ hideTOC }) => {
  const params = useParams();
  const currentLessonRef = useRef();
  const isMobile = useSmScreenMediaQuery();
  const { data: space } = useCurrentSpaceContext();
  const { lessonLabel, sectionLabel } = useCourseCustomLabels();
  const { data: spaceResource, isLoading } = useSpaceResource({
    spaceId: space?.id,
  });

  useEffect(() => {
    if (currentLessonRef.current) {
      currentLessonRef.current.scrollIntoView({
        block: "center",
        behavior: "instant",
      });
    }
  }, [currentLessonRef]);

  if (isLoading) {
    return <LoaderRow />;
  }
  const sections = spaceResource?.course_sections || [];

  return (
    <div className="overflow-auto py-2">
      {/* eslint-disable-next-line sonarjs/cognitive-complexity -- Already existed before maximum complexity value was reduced */}
      {sections.map(section => (
        <Disclosure key={section.id} defaultOpen>
          {({ open }) => (
            <>
              <Disclosure.Button className="text-dark flex w-full items-center gap-x-2 px-4 py-2.5 text-left">
                <Icon
                  size={16}
                  type={open ? "16-chevron-down" : "16-chevron-right"}
                />
                <Typography.LabelSm
                  as="h4"
                  weight="semibold"
                  color="text-darkest"
                >
                  {section.name}
                </Typography.LabelSm>
              </Disclosure.Button>
              <Disclosure.Panel className="space-y-0.5">
                {section.lessons.map(lesson => {
                  const isActiveLesson = lesson.id == params.lessonId;
                  return (
                    <NavLink
                      to={coursePaths.showLesson({
                        spaceSlug: params.spaceSlug,
                        lessonId: lesson.id,
                        sectionId: section.id,
                      })}
                      onClick={() => {
                        if (isMobile) {
                          hideTOC();
                        }
                      }}
                      className={classNames(
                        "hover:bg-secondary flex w-full cursor-pointer py-1.5 pl-11 pr-4",
                        {
                          "bg-secondary": isActiveLesson,
                        },
                      )}
                      key={lesson.id}
                      {...(isActiveLesson && {
                        ref: currentLessonRef,
                      })}
                    >
                      <TippyV2
                        content={drippedAtTippyContent({
                          drippedAt: section.dripped_at,
                          dripDelay: section.drip_delay,
                          needsToCompletePreviousLesson:
                            lesson.needs_to_complete_previous_lesson,
                          lessonLabel,
                          sectionLabel,
                        })}
                        placement="bottom-start"
                        disabled={lesson.is_dripped}
                        arrow={false}
                        className="text-dark flex w-full items-center gap-x-2"
                      >
                        <Icon
                          className={classNames(
                            isActiveLesson ? "text-default" : "text-light",
                            "shrink-0",
                          )}
                          size={16}
                          type={
                            !lesson.is_dripped
                              ? "16-lock"
                              : lesson.completed
                                ? "16-checked-green"
                                : "16-unchecked"
                          }
                        />
                        <div className="flex flex-1 items-center truncate">
                          <span className="truncate" title={lesson.name}>
                            <Typography.LabelSm
                              as="h5"
                              weight={isActiveLesson ? "medium" : "normal"}
                            >
                              {lesson.name}
                            </Typography.LabelSm>
                          </span>
                        </div>
                      </TippyV2>
                    </NavLink>
                  );
                })}
              </Disclosure.Panel>
            </>
          )}
        </Disclosure>
      ))}
    </div>
  );
};

TOCContent.propTypes = {
  hideTOC: PropTypes.func,
};
